import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { API_ENDPOINTS } from '@/utils/constants';
import { recordLatency, recordNetworkFailure } from './networkQuality';

type TimedConfig = InternalAxiosRequestConfig & { _start?: number };

/**
 * Axios instance configured for POS backend
 * 
 * Features:
 * - Base URL configured via environment variable
 * - Credentials included (for httpOnly cookies)
 * - Automatic token refresh on 401 errors
 * - Request/response interceptors
 */

const api: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_URL || '/api',
  withCredentials: true, // CRITICAL: Send cookies with requests
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000, // 30 seconds
});

/**
 * Token refresh management
 * Prevents multiple simultaneous refresh requests
 */
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: unknown) => void;
  reject: (reason?: any) => void;
}> = [];

const processQueue = (error: any = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve();
    }
  });

  failedQueue = [];
};

/**
 * Request Interceptor
 * - Logs outgoing requests in development
 */
api.interceptors.request.use(
  (config: TimedConfig) => {
    config._start = performance.now();
    if (import.meta.env.DEV) {
      console.log(`[API] ${config.method?.toUpperCase()} ${config.url}`);
    }
    return config;
  },
  (error: AxiosError) => {
    console.error('[API] Request error:', error);
    return Promise.reject(error);
  }
);

/**
 * Response Interceptor
 * - Handles token refresh on 401 errors
 * - Logs responses in development
 * - Redirects to login on auth failure
 */
api.interceptors.response.use(
  (response) => {
    const start = (response.config as TimedConfig)._start;
    if (start) recordLatency(performance.now() - start);
    if (import.meta.env.DEV) {
      console.log(`[API] Response ${response.status}:`, response.config.url);
    }
    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as TimedConfig & {
      _retry?: boolean;
    };

    if (error.response) {
      if (originalRequest?._start) recordLatency(performance.now() - originalRequest._start);
    } else if (error.message === 'Network Error') {
      recordNetworkFailure();
    }

    // Handle 401 Unauthorized (credential endpoints return 401 on bad OTP/PIN: never refresh/redirect)
    const isCredentialCall = /\/auth\/(login|pin-login)$/.test(originalRequest?.url ?? '');
    if (error.response?.status === 401 && !originalRequest._retry && !isCredentialCall) {
      // Avoid refresh loop
      if (originalRequest.url?.includes('/auth/refresh')) {
        isRefreshing = false;
        processQueue(error);
        
        // Redirect to login
        window.location.href = '/login';
        return Promise.reject(error);
      }

      if (isRefreshing) {
        // Queue the request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then(() => api(originalRequest))
          .catch(err => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        // Attempt to refresh token
        await axios.post(
          API_ENDPOINTS.REFRESH,
          {},
          {
            baseURL: api.defaults.baseURL,
            withCredentials: true,
          }
        );

        // Token refreshed successfully
        processQueue();
        isRefreshing = false;

        // Retry original request
        return api(originalRequest);
      } catch (refreshError) {
        // Refresh failed
        processQueue(refreshError);
        isRefreshing = false;

        // Clear auth state and redirect
        localStorage.clear();
        window.location.href = '/login';
        
        return Promise.reject(refreshError);
      }
    }

    // Handle other errors
    if (import.meta.env.DEV) {
      console.error('[API] Response error:', {
        status: error.response?.status,
        message: error.message,
        url: originalRequest?.url,
      });
    }

    return Promise.reject(error);
  }
);

/**
 * Generic API error handler
 * Extracts error message from API response
 */
export function getErrorMessage(error: unknown): string {
  if (axios.isAxiosError(error)) {
    // API error response
    const apiError = error.response?.data;
    if (apiError?.meta?.message) {
      return apiError.meta.message;
    }
    
    // Validation errors
    if (apiError?.meta?.errors) {
      const errors = Object.values(apiError.meta.errors);
      return errors.join(', ');
    }

    // HTTP error
    if (error.response?.status) {
      const statusMessages: Record<number, string> = {
        400: 'Invalid request',
        401: 'Unauthorized',
        403: 'Access denied',
        404: 'Resource not found',
        500: 'Server error',
        503: 'Service unavailable',
      };
      return statusMessages[error.response.status] || 'An error occurred';
    }

    // Network error
    if (error.message === 'Network Error') {
      return 'Network error. Please check your connection.';
    }

    return error.message;
  }

  // Generic error
  if (error instanceof Error) {
    return error.message;
  }

  return 'An unexpected error occurred';
}

/**
 * Check if error is a network error
 */
export function isNetworkError(error: unknown): boolean {
  return axios.isAxiosError(error) && error.message === 'Network Error';
}

/**
 * Check if error is a timeout
 */
export function isTimeoutError(error: unknown): boolean {
  return axios.isAxiosError(error) && error.code === 'ECONNABORTED';
}

export default api;
